import type { AxiosProgressEvent } from "axios";
import { z } from "zod";
import { BaseHttpService, validateWithSchema } from "@/api/http/BaseHttpService";
import { checkoutProgressSchema, checkoutTemplateSchema } from "@/api/http/schemas/checkoutSchemas";
import {
  applicationSchema,
  allApplicationsSchema,
  resumeLinkSchema,
  consentTemplateSchema,
  signedDocumentsSchema,
  duplicateApplicationSchema,
} from "@/api/http/schemas/applicationSchemas";
import type {
  Application,
  ApplicationListItem,
  ConsentTemplate,
  DuplicatedApplicationResponse,
  EnrichApplicationOptions,
  ResumeLink,
  SignedDocument,
} from "@/api/http/schemas/applicationSchemas";
import type { GenerateLinkPayload } from "@/types/CommonTypes";
import {
  documentsSchema,
  documentSchema,
  requiredDocumentsSchema,
} from "@/api/http/schemas/documentsSchemas";
import type {
  AddDocumentPayload,
  UpdateDocumentPayload,
  DeleteDocumentPayload,
  RequiredDocument,
  Document,
} from "@/api/http/schemas/documentsSchemas";
import type {
  CheckoutTemplate,
  CheckoutProgress,
  CheckoutDataValues,
  CustomStepUrlVisit,
} from "@/api/http/schemas/checkoutSchemas";
import type { UpdateConsentPayload } from "@/types/Checkout";

export const applicationEndpoints = {
  application: "borrower_platform/applications/:id",
  applicationsList: "borrower_platform/applications",
  checkout: {
    index: "borrower_platform/applications/:id/checkout",
    progress: "borrower_platform/applications/:id/checkout/progress",
    services: {
      chart: "borrower_platform/applications/:id/checkout/services/chart",
      vouched: "borrower_platform/applications/:id/checkout/services/vouched_verification",
    },
  },
  consent: "borrower_platform/applications/:id/consent",
  consent_templates: {
    single: "borrower_platform/applications/:appId/consent_template/:consentId",
  },
  documents: {
    list: "borrower_platform/applications/:id/files",
    listRequired: "borrower_platform/applications/:id/required_documents",
    upload: "borrower_platform/applications/:id/files",
    update: "borrower_platform/applications/:id/files/:id",
    delete: "borrower_platform/applications/:id/files/:id",
  },
  duplicate: "borrower_platform/applications/:id/duplicate",
  enrich: "borrower_platform/applications/:id/checkout/services/enrich",
  links: "borrower_platform/applications/:id/links",
  resumeLink: "borrower_platform/applications/:id/resume_link",
  signed_documents: {
    index: "borrower_platform/applications/:id/signed_documents",
  },
};

export class ApplicationsHttpService extends BaseHttpService {
  screenLoadingPaths = [applicationEndpoints.applicationsList];

  async getApplicationsList() {
    const { data } = await this.get<{ data: ApplicationListItem[] }>(
      applicationEndpoints.applicationsList
    );

    return validateWithSchema(allApplicationsSchema, data);
  }

  async getApplication(applicationId: string, params?: Record<string, unknown>) {
    const { data } = await this.get<{ data: Application }>(
      applicationEndpoints.application.replace(":id", applicationId),
      { params }
    );

    return validateWithSchema(applicationSchema, data);
  }

  async generateResumeLink(applicationId: string, payload: GenerateLinkPayload) {
    const { data } = await this.post<{ data: ResumeLink }>(
      applicationEndpoints.resumeLink.replace(":id", applicationId),
      payload
    );

    return validateWithSchema(resumeLinkSchema, data).url;
  }

  async duplicate(applicationId: Application["id"]) {
    const { data } = await this.post<{ data: DuplicatedApplicationResponse }>(
      applicationEndpoints.duplicate.replace(":id", applicationId)
    );

    return validateWithSchema(duplicateApplicationSchema, data);
  }

  async getCheckout(applicationId: string) {
    const data = await this.get<{ data: CheckoutTemplate }>(
      applicationEndpoints.checkout.index.replace(":id", applicationId)
    );

    return validateWithSchema(checkoutTemplateSchema, data.data);
  }

  async updateCheckoutData(applicationId: string, payload: CheckoutDataValues) {
    const data = await this.put<{ data: CheckoutTemplate }>(
      applicationEndpoints.checkout.index.replace(":id", applicationId),
      { data: payload }
    );

    return validateWithSchema(checkoutTemplateSchema, data.data);
  }

  async getCheckoutProgress(applicationId: string) {
    const data = await this.get<{ data: CheckoutProgress }>(
      applicationEndpoints.checkout.progress.replace(":id", applicationId)
    );

    return validateWithSchema(checkoutProgressSchema, data.data);
  }

  async getConsent(applicationId: string, consentId: string) {
    const { data } = await this.get<{ data: ConsentTemplate }>(
      applicationEndpoints.consent_templates.single
        .replace(":appId", applicationId)
        .replace(":consentId", consentId)
    );

    return validateWithSchema(consentTemplateSchema, data);
  }

  async updateConsent(applicationId: Application["id"], payload: UpdateConsentPayload) {
    await this.post(applicationEndpoints.consent.replace(":id", applicationId), payload);
  }

  async getUploadedDocuments(
    applicationId: Application["id"],
    params?: Record<string, unknown>
  ): Promise<Document[]> {
    const { data } = await this.get<{ data: Document[] }>(
      applicationEndpoints.documents.list.replace(":id", applicationId),
      {
        params,
      }
    );

    return validateWithSchema(documentsSchema, data);
  }

  async getSignedDocuments(applicationId: Application["id"], params?: Record<string, unknown>) {
    const { data } = await this.get<{ data: SignedDocument[] }>(
      applicationEndpoints.signed_documents.index.replace(":id", applicationId),
      { params }
    );

    return validateWithSchema(signedDocumentsSchema, data);
  }

  async getLinks(applicationId: Application["id"], params?: Record<string, unknown>) {
    const { data } = await this.get<{ data: { contract_link: string } }>(
      applicationEndpoints.links.replace(":id", applicationId),
      {
        params,
      }
    );

    return validateWithSchema(z.string(), data.contract_link);
  }

  async getRequiredDocuments(
    applicationId: Application["id"],
    params?: Record<string, unknown>
  ): Promise<RequiredDocument[]> {
    const { data } = await this.get<{ data: RequiredDocument[] }>(
      applicationEndpoints.documents.listRequired.replace(":id", applicationId),
      { params }
    );

    return validateWithSchema(requiredDocumentsSchema, data);
  }

  async addDocuments(
    applicationId: Application["id"],
    payload: AddDocumentPayload,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<Document[]> {
    const formData = new FormData();

    payload.forEach((file, i) => {
      Object.keys(file).forEach((key) =>
        formData.append(
          `files[${i}][${key}]`,
          file[key as keyof AddDocumentPayload[number]] as string | Blob
        )
      );
    });

    const { data } = await this.post<{ data: Document }>(
      applicationEndpoints.documents.upload.replace(":id", applicationId),
      formData,
      {
        headers: {
          "content-type": "multipart/form-data",
        },
        onUploadProgress,
      }
    );

    return validateWithSchema(documentsSchema, data);
  }

  async updateDocument(
    applicationId: Application["id"],
    fileId: number,
    payload: UpdateDocumentPayload
  ): Promise<Document> {
    const { data } = await this.put<{ data: Document }>(
      applicationEndpoints.documents.update
        .replace(":id", applicationId)
        .replace(":id", String(fileId)),
      payload
    );

    return validateWithSchema(documentSchema, data);
  }

  async deleteDocument(
    applicationId: Application["id"],
    fileId: Document["id"],
    payload: DeleteDocumentPayload
  ) {
    await this.delete<void>(
      applicationEndpoints.documents.delete
        .replace(":id", applicationId)
        .replace(":id", String(fileId)),
      payload
    );
  }

  async getChartClientId(applicationId: string) {
    const { data } = await this.get<{ data: { client_id: string } }>(
      applicationEndpoints.checkout.services.chart.replace(":id", applicationId)
    );

    return data.client_id;
  }

  async enrichApplication(
    applicationId: string,
    providers: string[],
    options?: EnrichApplicationOptions
  ) {
    await this.put(applicationEndpoints.enrich.replace(":id", applicationId), {
      providers,
      options,
    });
  }

  async getVouchedConfig(applicationId: string) {
    const { data } = await this.get<{
      data: { public_key: string; callback_url: string };
    }>(applicationEndpoints.checkout.services.vouched.replace(":id", applicationId));
    return data;
  }

  async getFile(fileUrl: string, params?: Record<string, unknown>) {
    return await this.get<Blob>(fileUrl, params);
  }

  async recordCustomStepUrlVisit(applicationId: string, urlVisit: CustomStepUrlVisit) {
    await this.put(applicationEndpoints.checkout.index.replace(":id", applicationId), {
      custom_step_url_visits: [urlVisit],
    });
  }
}
